import type React from 'react';
import { useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { Layers, Minus, Plus } from 'lucide-react';
import { ConfirmDialog } from '../../docs/components/ConfirmDialog';
import type { Capability } from '../../../../types/admin';
import { serviceGroupOf } from '../utils';

interface ServicePresetCardProps {
  capabilities: Capability[];
  assignedCodes: Set<string>;
  onAdd: (capabilities: Capability[]) => void;
  onRemove: (capabilityCodes: string[]) => void;
}

/**
 * Presets built from `configSchema.ui.serviceGroup` (e.g. Generate Video = text_to_video +
 * image_to_video): assign or remove a whole service in one click.
 */
export const ServicePresetCard: React.FC<ServicePresetCardProps> = ({
  capabilities,
  assignedCodes,
  onAdd,
  onRemove,
}) => {
  const [pendingRemoval, setPendingRemoval] = useState<{ group: string; codes: string[] } | null>(
    null
  );
  const groups = useMemo(() => {
    const byGroup = new Map<string, Capability[]>();
    for (const capability of capabilities) {
      const group = serviceGroupOf(capability.effectiveConfigSchema ?? capability.configSchema);
      if (group) byGroup.set(group, [...(byGroup.get(group) ?? []), capability]);
    }
    return [...byGroup.entries()];
  }, [capabilities]);

  if (groups.length === 0) return null;

  return (
    <Box className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
      <Box className="mb-3 flex items-center gap-2">
        <Layers size={18} className="text-blue-500" />
        <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
          Service presets
        </Typography>
      </Box>
      <Box className="space-y-3">
        {groups.map(([group, members]) => {
          const assigned = members.filter((capability) => assignedCodes.has(capability.code));
          const complete = assigned.length === members.length;
          return (
            <Box
              key={group}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 p-3 dark:border-slate-700"
            >
              <Box className="min-w-0">
                <Typography className="font-medium text-gray-900 dark:text-slate-100">
                  {group}
                </Typography>
                <Box className="mt-1 flex flex-wrap gap-1">
                  {members.map((capability) => (
                    <Chip
                      key={capability.code}
                      size="small"
                      label={capability.code}
                      color={assignedCodes.has(capability.code) ? 'primary' : 'default'}
                      variant={assignedCodes.has(capability.code) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
              <Box className="flex gap-2">
                {!complete && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Plus size={16} />}
                    onClick={() => onAdd(members)}
                    sx={{ textTransform: 'none' }}
                  >
                    Add all
                  </Button>
                )}
                {assigned.length > 0 && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<Minus size={16} />}
                    onClick={() =>
                      setPendingRemoval({ group, codes: assigned.map((item) => item.code) })
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Remove all
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title="Remove service from package"
        message={`Remove ${pendingRemoval?.codes.join(', ')} (${pendingRemoval?.group}) from this package? Changes apply after Save Changes.`}
        confirmLabel="Remove"
        color="error"
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) onRemove(pendingRemoval.codes);
          setPendingRemoval(null);
        }}
      />
    </Box>
  );
};
